import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addParameterToWorkingList,
  createWorkingList,
  deleteWorkingList,
  duplicateWorkingList,
  getWorkingLists,
  getWorkingListSelection,
  removeParameterFromWorkingList,
  updateWorkingList,
  updateWorkingListSelection,
} from '../../../../services/catalogService';

function sortLists(lists) {
  return [...lists].sort((left, right) => {
    if (left.isPrivate !== right.isPrivate) {
      return left.isPrivate ? -1 : 1;
    }
    if (left.isDefault !== right.isDefault) {
      return left.isDefault ? -1 : 1;
    }
    return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
  });
}

const DEFAULT_WORKING_LIST_SELECTION = {
  activeListIds: [],
  showOnlyActiveListParams: false,
  listComparisonFilter: 'all',
};

function normalizeWorkingListSelection(selection, lists) {
  const validListIds = new Set((lists || []).map(list => list.id));
  const activeListIds = Array.isArray(selection?.activeListIds)
    ? selection.activeListIds.filter(id => validListIds.has(id)).slice(0, 2)
    : [];

  return {
    activeListIds,
    showOnlyActiveListParams: Boolean(selection?.showOnlyActiveListParams && activeListIds.length > 0),
    listComparisonFilter:
      activeListIds.length === 2
        ? (selection?.listComparisonFilter || 'all')
        : 'all',
  };
}

const useWorkingLists = () => {
  const { t } = useTranslation();
  const [parameterLists, setParameterLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionLoading, setSelectionLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workingListSelection, setWorkingListSelection] = useState(DEFAULT_WORKING_LIST_SELECTION);

  // Haelt den jeweils aktuellsten Stand fest (#255, Runde 4): `handleFavoriteToggle`
  // legt bei Bedarf eine Liste an und fuegt ihr im selben Zug einen Parameter hinzu.
  // Die noch laufende Kette (`.then(applyToggle)`) haelt dabei den `addToList`-
  // Funktionszeiger aus dem Render *vor* der Anlage, dessen `parameterLists`-Closure
  // noch leer ist -- und React committet die Statusaenderung aus `createList` nicht
  // synchron, sondern erst nach dem laufenden Mikrotask, in dem `applyToggle` schon
  // laeuft. Ein `useEffect`, das die Ref aus `parameterLists` nachzieht, kaeme also
  // ebenfalls zu spaet. Die Ref wird deshalb synchron an jeder Schreibstelle
  // (`setParameterListsAndRef`) mitgefuehrt, nicht ueber einen Effekt aus dem
  // gerenderten Zustand abgeleitet (CI-Fund, Pipeline 499).
  const parameterListsRef = useRef(parameterLists);
  const setParameterListsAndRef = (nextLists) => {
    parameterListsRef.current = nextLists;
    setParameterLists(nextLists);
  };

  useEffect(() => {
    let cancelled = false;

    async function loadLists() {
      setLoading(true);
      setSelectionLoading(true);
      setError(null);
      const [result, selectionResult] = await Promise.all([
        getWorkingLists(),
        getWorkingListSelection(),
      ]);
      if (cancelled) {
        return;
      }

      if (result.source === 'api') {
        const sortedLists = sortLists(result.lists);
        setParameterListsAndRef(sortedLists);
        setWorkingListSelection(normalizeWorkingListSelection(selectionResult.selection, sortedLists));
        setLoading(false);
        setSelectionLoading(false);
        return;
      }

      setParameterListsAndRef([]);
      setWorkingListSelection(DEFAULT_WORKING_LIST_SELECTION);
      setError(result.error || new Error('Failed to load working lists'));
      setLoading(false);
      setSelectionLoading(false);
    }

    loadLists();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setWorkingListSelection(prevSelection => {
      const normalizedSelection = normalizeWorkingListSelection(prevSelection, parameterLists);
      const hasChanges =
        JSON.stringify(normalizedSelection.activeListIds) !== JSON.stringify(prevSelection.activeListIds)
        || normalizedSelection.showOnlyActiveListParams !== prevSelection.showOnlyActiveListParams
        || normalizedSelection.listComparisonFilter !== prevSelection.listComparisonFilter;

      return hasChanges ? normalizedSelection : prevSelection;
    });
  }, [parameterLists]);
  const replaceListInState = (updatedList) => {
    if (!updatedList) {
      return;
    }

    const prevLists = parameterListsRef.current;
    const existingIndex = prevLists.findIndex(list => list.id === updatedList.id);
    const nextLists = existingIndex === -1
      ? sortLists([...prevLists, updatedList])
      : sortLists(prevLists.map((list, index) => (index === existingIndex ? updatedList : list)));

    setParameterListsAndRef(nextLists);
  };

  const removeListFromState = (listId) => {
    setParameterListsAndRef(parameterListsRef.current.filter(list => list.id !== listId));
  };

  const persistWorkingListSelection = useCallback(async (selectionUpdate) => {
    const normalizedSelection = normalizeWorkingListSelection(selectionUpdate, parameterLists);
    const hasChanges =
      JSON.stringify(normalizedSelection.activeListIds) !== JSON.stringify(workingListSelection.activeListIds)
      || normalizedSelection.showOnlyActiveListParams !== workingListSelection.showOnlyActiveListParams
      || normalizedSelection.listComparisonFilter !== workingListSelection.listComparisonFilter;

    if (!hasChanges) {
      return workingListSelection;
    }

    const persistedSelection = await updateWorkingListSelection(normalizedSelection);
    const nextSelection = normalizeWorkingListSelection(persistedSelection, parameterLists);
    setWorkingListSelection(nextSelection);
    return nextSelection;
  }, [parameterLists, workingListSelection]);
  const isParameterInAnyList = (parameterId) =>
    parameterLists.some(list => list.parameterIds.includes(parameterId));

  const isParameterInActiveList = (parameterId, activeWorkingList) =>
    activeWorkingList?.some(listId => {
      const list = parameterLists.find(entry => entry.id === listId);
      return Boolean(list?.parameterIds.includes(parameterId));
    }) || false;

  const hasParameterPendingInActiveList = (parameterId, activeWorkingList) =>
    activeWorkingList?.some(listId => {
      const list = parameterLists.find(entry => entry.id === listId);
      return Boolean(list?.additionRequests.some(request => request.parameterId === parameterId));
    }) || false;

  const isParameterInList = (parameterId, listId) => {
    const list = parameterLists.find(entry => entry.id === listId);
    return Boolean(list?.parameterIds.includes(parameterId));
  };

  const hasParameterPendingApproval = (parameterId, listId) => {
    const list = parameterLists.find(entry => entry.id === listId);
    return Boolean(list?.additionRequests.some(request => request.parameterId === parameterId));
  };

  const handleAddToList = async (parameterId, listId, parameterName = '') => {
    const list = parameterListsRef.current.find(entry => entry.id === listId);
    if (!list) {
      return { success: false, severity: 'error', message: t('dataCatalog.status.apiUnavailable') };
    }

    try {
      const result = await addParameterToWorkingList(listId, parameterId);
      replaceListInState(result.list);

      if (result.action === 'already_exists') {
        return {
          success: false,
          info: true,
          severity: 'info',
          message: t('dataCatalog.messages.alreadyInList', {
            parameter: parameterName,
            list: list.name,
          }),
        };
      }

      if (result.action === 'request_created') {
        return {
          success: true,
          info: true,
          severity: 'warning',
          message: t('dataCatalog.messages.suggestionSent', {
            parameter: parameterName,
            list: list.name,
          }),
        };
      }

      if (result.action === 'request_exists') {
        return {
          success: false,
          info: true,
          severity: 'info',
          message: t('dataCatalog.messages.pendingSuggestion', {
            parameter: parameterName,
            list: list.name,
          }),
        };
      }

      return {
        success: true,
        severity: 'success',
        message: t('dataCatalog.messages.addedGeneric', {
          parameter: parameterName,
          list: list.name,
        }),
      };
    } catch (requestError) {
      return {
        success: false,
        severity: 'error',
        message: requestError.message || t('dataCatalog.status.apiUnavailable'),
      };
    }
  };

  const handleRemoveFromList = async (parameterId, listId, parameterName = '') => {
    const list = parameterListsRef.current.find(entry => entry.id === listId);
    if (!list) {
      return { success: false, severity: 'error', message: t('dataCatalog.status.apiUnavailable') };
    }

    try {
      const result = await removeParameterFromWorkingList(listId, parameterId);
      replaceListInState(result.list);

      if (result.action === 'not_found') {
        return {
          success: false,
          info: true,
          severity: 'info',
          message: result.message || t('dataCatalog.status.apiUnavailable'),
        };
      }

      return {
        success: true,
        severity: 'success',
        message: t('dataCatalog.messages.removedFromList', {
          parameter: parameterName,
          list: list.name,
        }),
      };
    } catch (requestError) {
      return {
        success: false,
        severity: 'error',
        message: requestError.message || t('dataCatalog.status.apiUnavailable'),
      };
    }
  };

  const handleToggleParameterInList = async (parameterId, listId, parameterName = '') => {
    const list = parameterListsRef.current.find(entry => entry.id === listId);
    if (!list) {
      return { success: false, severity: 'error', message: t('dataCatalog.status.apiUnavailable') };
    }

    if (list.isPrivate) {
      if (isParameterInList(parameterId, listId)) {
        return handleRemoveFromList(parameterId, listId, parameterName);
      }
      return handleAddToList(parameterId, listId, parameterName);
    }

    if (hasParameterPendingApproval(parameterId, listId)) {
      return {
        success: false,
        info: true,
        severity: 'info',
        message: t('dataCatalog.messages.pendingSuggestion', {
          parameter: parameterName,
          list: list.name,
        }),
      };
    }

    return handleAddToList(parameterId, listId, parameterName);
  };

  const createList = async ({ name, listType, isPrivate }) => {
    const createdList = await createWorkingList({ name, listType, isPrivate });
    replaceListInState(createdList);
    return createdList;
  };

  const renameList = async (listId, name) => {
    const updatedList = await updateWorkingList(listId, { name });
    replaceListInState(updatedList);
    return updatedList;
  };

  const deleteList = async (listId) => {
    await deleteWorkingList(listId);
    removeListFromState(listId);
  };

  const duplicateList = async (listId) => {
    const duplicatedList = await duplicateWorkingList(listId);
    replaceListInState(duplicatedList);
    return duplicatedList;
  };

  return {
    parameterLists,
    loading,
    selectionLoading,
    error,
    workingListSelection,
    persistWorkingListSelection,
    isParameterInAnyList,
    isParameterInActiveList,
    hasParameterPendingInActiveList,
    isParameterInList,
    hasParameterPendingApproval,
    handleAddToList,
    handleRemoveFromList,
    handleToggleParameterInList,
    createList,
    renameList,
    deleteList,
    duplicateList,
  };
};

export default useWorkingLists;
